/**
 * The mutable data tree the engine prepares for one Character, shaped like Foundry's character
 * `system` (and `flags`) so ActiveEffectLike paths from the Snapshot address it unchanged.
 * `flags.pf2e` and `flags.system` are the same object, as in Foundry.
 */
import type { IWRData } from "./iwr";
import type { RawPredicate } from "./predicate";
import type { Attribute, MovementType, ProficiencyRankName, Rank, SaveSlug, SenseAcuity, SkillSlug } from "./values";
import { ARMOR_CATEGORIES, ATTRIBUTES, MOVEMENT_TYPES, SAVES, SAVE_ATTRIBUTES, SKILLS, SKILL_SLUGS, WEAPON_CATEGORIES } from "./values";

export interface AbilityData {
  mod: number;
  /** The integer modifier before Rule Elements touched it. */
  base: number;
}

export interface SenseData {
  type: string;
  acuity: SenseAcuity;
  /** Feet; Infinity when unlimited. */
  range: number;
  source: string | null;
}

/**
 * One attack or defense proficiency (Foundry: `MartialProficiency`): a weapon or armour category,
 * or a custom proficiency a MartialProficiency Rule Element added, whose `definition` says which
 * items it covers. An ActiveEffectLike may create a partial record, so every field but `rank`
 * is optional here and filled in when the proficiencies are prepared.
 */
export interface MartialProficiencyData {
  rank: Rank;
  label?: string;
  visible?: boolean;
  /** A predicate over an item's roll options: which weapons or armour count for this proficiency. */
  definition?: RawPredicate;
  /** The category whose rank this proficiency follows. */
  sameAs?: string;
  /** The highest rank a `sameAs` link may reach. */
  maxRank?: ProficiencyRankName;
  /** The proficiency bonus at the Character's Level, once prepared. */
  value?: number;
  breakdown?: string;
  /** The Entry that added this proficiency, for custom ones. */
  source?: string | null;
  sourceName?: string | null;
  custom?: boolean;
}

/** One movement type's prepared speed, as Foundry writes it back to `system.movement.speeds`. */
export interface SpeedData {
  type?: MovementType;
  value: number;
  base: number;
  source: string | null;
  label?: string;
  breakdown?: string;
}

export interface SkillData {
  rank: Rank;
  attribute: Attribute;
  armor: boolean;
  lore: boolean;
  label: string;
}

export interface ClassDCData {
  label: string;
  attribute: Attribute;
  rank: Rank;
  primary: boolean;
}

export interface ResourceData {
  value: number;
  max: number;
}

/** A crafting entry a CraftingAbility Rule Element registers (Foundry: CraftingAbilityData). */
export interface CraftingEntryData {
  slug: string;
  resource: string | null;
  label: string;
  isAlchemical: boolean;
  isDailyPrep: boolean;
  isPrepared: boolean;
  batchSize: number;
  craftableItems: { predicate: RawPredicate; batchSize?: number }[];
  maxItemLevel: number;
  maxSlots: number | null;
  preparedFormulaData: { uuid: string; quantity?: number; expended?: boolean; isSignatureItem?: boolean }[];
  source: string | null;
  sourceName: string | null;
}

export interface AutoChange {
  mode: string;
  level: number | null;
  value: boolean | number | string | null;
  source: string;
}

export interface ActorSystemData {
  abilities: Record<Attribute, AbilityData>;
  attributes: {
    ancestryhp: number;
    classhp: number;
    hp: {
      value: number;
      max: number;
      temp: number;
      negativeHealing: boolean;
      unrecoverable: number;
      recoveryMultiplier: number;
      recoveryAddend: number;
    };
    ac: { value: number };
    reach: { base: number; manipulate: number };
    shield: { itemId: string | null; raised: boolean; broken: boolean; destroyed: boolean; ac: number; name: string; hp: { value: number; max: number }; hardness: number; brokenThreshold: number };
    flanking: { canFlank: boolean; canGangUp: unknown[]; flankable: boolean; offGuardable: boolean };
    dying: { value: number; max: number; recoveryDC: number };
    wounded: { value: number; max: number };
    doomed: { value: number; max: number };
    familiarAbilities: { value: number };
    handsFree: number;
    classDC: { rank: Rank; value: number } | null;
    classOrSpellDC: { rank: Rank; value: number };
    /** The best Spellcasting Entry DC (#10), when the Character casts. */
    spellDC: { rank: Rank; value: number } | null;
    speed: { value: number };
    immunities: IWRData[];
    weaknesses: IWRData[];
    resistances: IWRData[];
  };
  build: {
    attributes: {
      manual: boolean;
      keyOptions: Attribute[];
      boosts: { ancestry: Attribute[]; background: Attribute[]; class: Attribute | null; 1: Attribute[]; 5: Attribute[]; 10: Attribute[]; 15: Attribute[]; 20: Attribute[] };
      flaws: { ancestry: Attribute[] };
      apex: Attribute | null;
    };
    languages: { value: number; max: number; granted: { slug: string; source: string }[] };
  };
  details: {
    level: { value: number };
    keyability: { value: Attribute };
    ancestry: { name: string; trait: string; countsAs: string[] } | null;
    heritage: { name: string; trait: string } | null;
    class: { name: string; trait: string } | null;
    /** The held deity (Foundry: `details.deities`): its skills, favoured weapons, fonts and domains by slug with labels. */
    deities: { primary: { slug: string; name: string; skill: string[]; weapons: string[]; font: string[]; spells: Record<string, string> } | null; domains: Record<string, string> };
    languages: { value: string[] };
    alliance: string;
  };
  perception: { rank: Rank; value: number; senses: SenseData[] };
  proficiencies: {
    attacks: Record<string, MartialProficiencyData>;
    defenses: Record<string, MartialProficiencyData>;
    classDCs: Record<string, ClassDCData>;
    spellcasting: { rank: Rank };
    aliases: Record<string, string>;
  };
  saves: Record<SaveSlug, { rank: Rank; attribute: Attribute; value: number }>;
  skills: Record<string, SkillData & { value: number }>;
  resources: Record<string, ResourceData | unknown> & {
    heroPoints: ResourceData;
    focus: ResourceData & { cap: number };
    investiture: ResourceData;
    crafting: { infusedReagents: ResourceData };
    mythicPoints: ResourceData;
  };
  traits: { value: string[]; rarity: string; size: { value: string }; naturalSize?: string };
  movement: { speeds: { land: SpeedData } & Partial<Record<Exclude<MovementType, "land">, SpeedData | null>>; terrain: Record<string, unknown> };
  initiative: { statistic: string; tiebreakPriority: number };
  crafting: { formulas: unknown[]; entries: Record<string, CraftingEntryData> };
  hands: { max: { value: number; active: number }; free: { value: number; really: number } };
  autoChanges: Record<string, AutoChange[]>;
  exploration: unknown[];
}

export interface ActorData {
  system: ActorSystemData;
  flags: { pf2e: Record<string, unknown>; system: Record<string, unknown> };
  inventory: { bulk: { encumberedAfterAddend: number; maxAddend: number } };
}

export function createActorData(level: number): ActorData {
  const abilities = Object.fromEntries(ATTRIBUTES.map((a) => [a, { mod: 0, base: 0 }])) as Record<Attribute, AbilityData>;
  const saves = Object.fromEntries(SAVES.map((s) => [s, { rank: 0, attribute: SAVE_ATTRIBUTES[s], value: 0 }])) as ActorSystemData["saves"];
  const skills: ActorSystemData["skills"] = {};
  for (const slug of SKILL_SLUGS) {
    const { attribute, label } = SKILLS[slug as SkillSlug];
    skills[slug] = { rank: 0, attribute, armor: attribute === "dex" || attribute === "str", lore: false, label, value: 0 };
  }
  const attacks: Record<string, MartialProficiencyData> = Object.fromEntries(WEAPON_CATEGORIES.map((c) => [c, { rank: 0 as Rank, visible: true }]));
  const defenses: Record<string, MartialProficiencyData> = Object.fromEntries(ARMOR_CATEGORIES.map((c) => [c, { rank: 0 as Rank, visible: true }]));
  const flags: Record<string, unknown> = {
    rulesSelections: {},
    favoredWeaponRank: 0,
    freeCrafting: false,
    quickAlchemy: false,
    showBasicUnarmed: true,
    featLimits: {},
    rollOptions: {},
  };
  return {
    system: {
      abilities,
      attributes: {
        ancestryhp: 0,
        classhp: 0,
        hp: { value: 0, max: 0, temp: 0, negativeHealing: false, unrecoverable: 0, recoveryMultiplier: 1, recoveryAddend: 0 },
        ac: { value: 10 },
        reach: { base: 5, manipulate: 5 },
        shield: { itemId: null, raised: false, broken: false, destroyed: false, ac: 0, name: "Shield", hp: { value: 0, max: 0 }, hardness: 0, brokenThreshold: 0 },
        flanking: { canFlank: false, canGangUp: [], flankable: false, offGuardable: false },
        dying: { value: 0, max: 4, recoveryDC: 10 },
        wounded: { value: 0, max: 3 },
        doomed: { value: 0, max: 3 },
        familiarAbilities: { value: 0 },
        handsFree: 2,
        classDC: null,
        classOrSpellDC: { rank: 0, value: 0 },
        spellDC: null,
        speed: { value: 25 },
        immunities: [],
        weaknesses: [],
        resistances: [],
      },
      build: {
        attributes: {
          manual: false,
          keyOptions: [],
          boosts: { ancestry: [], background: [], class: null, 1: [], 5: [], 10: [], 15: [], 20: [] },
          flaws: { ancestry: [] },
          apex: null,
        },
        languages: { value: 0, max: 0, granted: [] },
      },
      details: {
        level: { value: level },
        keyability: { value: "str" },
        ancestry: null,
        heritage: null,
        class: null,
        deities: { primary: null, domains: {} },
        languages: { value: [] },
        alliance: "party",
      },
      perception: { rank: 0, value: 0, senses: [] },
      proficiencies: { attacks, defenses, classDCs: {}, spellcasting: { rank: 0 }, aliases: {} },
      saves,
      skills,
      resources: {
        heroPoints: { value: 1, max: 3 },
        focus: { value: 0, max: 0, cap: 3 },
        investiture: { value: 0, max: 10 },
        crafting: { infusedReagents: { value: 0, max: 0 } },
        mythicPoints: { value: 0, max: 0 },
      },
      traits: { value: [], rarity: "unique", size: { value: "med" } },
      movement: { speeds: { land: { type: "land", value: 25, base: 25, source: null }, ...Object.fromEntries(MOVEMENT_TYPES.filter((t) => t !== "land").map((t) => [t, null])) }, terrain: {} },
      initiative: { statistic: "perception", tiebreakPriority: 1 },
      crafting: { formulas: [], entries: {} },
      hands: { max: { value: 2, active: 2 }, free: { value: 2, really: 2 } },
      autoChanges: {},
      exploration: [],
    },
    flags: { pf2e: flags, system: flags },
    inventory: { bulk: { encumberedAfterAddend: 0, maxAddend: 0 } },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Foundry's `getProperty`: walk a dot path; undefined when any segment is missing. */
export function getProperty(object: unknown, path: string): unknown {
  if (!path) return object;
  let target: unknown = object;
  for (const segment of path.split(".")) {
    if (!isRecord(target)) return undefined;
    if (!(segment in target)) return undefined;
    target = (target as Record<string, unknown>)[segment];
  }
  return target;
}

/** Foundry's `setProperty`: creates intermediate objects. Returns whether the value changed. */
export function setProperty(object: Record<string, unknown>, path: string, value: unknown): boolean {
  const parts = path.split(".");
  const key = parts.pop()!;
  let target: Record<string, unknown> = object;
  for (const part of parts) {
    const next = target[part];
    if (!isRecord(next)) {
      const created: Record<string, unknown> = {};
      target[part] = created;
      target = created;
    } else {
      target = next as Record<string, unknown>;
    }
  }
  if (!(key in target) || target[key] !== value) {
    target[key] = value;
    return true;
  }
  return false;
}
